import { useEffect, useRef } from 'react';

/**
 * Confirm-on-irreversible — the §6 ledger's third row.
 *
 * ## Why it is inline and not a dialog
 *
 * It appears **in the row it is about** (design 458–465, 1376–1381): the card
 * whose order is being cancelled, the tray whose batch is about to overwrite.
 * A modal takes the thing being decided off the screen at the moment of
 * deciding, which is exactly wrong under a five-minute clock — the operator's
 * last look should be at the object, not at a sentence describing it.
 *
 * ## Designed for Phase 4's RELEASE, not for this phase's cancel
 *
 * M5-D3 lifts this for MW RELEASE, which is the demanding case: irreversible,
 * timed, and wrong is expensive. Three things follow, and all three are here
 * before there is a second caller:
 *
 *  - **`detail` carries a value, not reassurance.** RELEASE needs "STAGE 1 ·
 *    CREDIT +4" beside the question, because the price of acting now is the
 *    whole decision. The copy rules allow a second line only for a real
 *    consequence with a number in it, and this is that line.
 *  - **The confirm button is focused on open.** A confirm reached by keyboard
 *    under a clock must not need the mouse, and `⏎` lands on the deliberate
 *    action rather than on whatever had focus before.
 *  - **The safe action is first in the DOM and `Escape` takes it.** Shift-Tab
 *    from the confirm reaches KEEP; Escape is the same answer without aiming.
 *
 * What it deliberately does **not** do is decide anything. It renders a
 * question and emits two callbacks; the caller owns the state, so two of these
 * can never be open with one answer between them.
 */

export interface ConfirmInlineProps {
  /** One short question. Never a restatement of the rule being applied. */
  readonly prompt: string;
  /** A real consequence with a value in it, or nothing at all. */
  readonly detail?: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly testId?: string;
}

export function ConfirmInline(props: ConfirmInlineProps): JSX.Element {
  const { onCancel } = props;
  const cancelRef = useRef<HTMLButtonElement>(null);

  /*
   * The **safe** action takes focus, not the confirm.
   *
   * It used to be the other way round, on the reasoning that a confirm reached
   * by keyboard under a clock must not need the mouse. True, but it collided
   * with the muscle memory this console spends the whole window building: in
   * the order picker Enter takes the first row, so a hand that has queued four
   * orders arrives at a destructive question with Enter already loaded. KEEP is
   * still one keystroke from the confirm — Tab, then Enter — and Escape is the
   * same answer without aiming.
   */
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // Innermost layer wins: the shell's own Escape must not close the picker
      // behind a confirm the operator is still looking at.
      event.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onCancel]);

  return (
    <div
      data-testid={props.testId ?? 'confirm-inline'}
      role="group"
      aria-label={props.prompt}
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 30,
        padding: '4px 8px 4px 12px',
        background: 'var(--red-surface)',
        borderTop: '1px solid var(--red)',
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-prose)',
          fontSize: 12,
          color: 'var(--red-text)',
        }}
      >
        <span data-testid="confirm-prompt">{props.prompt}</span>
        {props.detail !== undefined && (
          <span
            data-testid="confirm-detail"
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 10.5,
              letterSpacing: '.1em',
              opacity: 0.85,
            }}
          >
            {props.detail}
          </span>
        )}
      </span>
      <button
        ref={cancelRef}
        type="button"
        data-testid="confirm-cancel"
        onClick={props.onCancel}
        style={{
          ...BUTTON,
          background: 'transparent',
          border: '1px solid var(--border-strong)',
          color: 'var(--text-2)',
        }}
      >
        {props.cancelLabel ?? 'KEEP'}
      </button>
      <button
        type="button"
        data-testid="confirm-accept"
        onClick={props.onConfirm}
        style={{
          ...BUTTON,
          background: 'var(--red)',
          border: '1px solid var(--red)',
          color: 'var(--red-on)',
          fontWeight: 700,
        }}
      >
        {props.confirmLabel}
      </button>
    </div>
  );
}

const BUTTON = {
  flex: 'none',
  height: 22,
  padding: '0 8px',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--font-ui)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  cursor: 'pointer',
} as const;
